/* Trim transparent export margins without flattening SVGs or removing white artwork. */
(() => {
  'use strict';
  const cache = new Map();
  const read = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const load = async src => {
    const image = new Image();
    image.src = src;
    await image.decode();
    return image;
  };
  function visiblePixels(image, limit) {
    const scale = Math.min(1, limit / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    let left = canvas.width, top = canvas.height, right = -1, bottom = -1;
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        if (pixels[(y * canvas.width + x) * 4 + 3] === 0) continue;
        left = Math.min(left, x); right = Math.max(right, x);
        top = Math.min(top, y); bottom = Math.max(bottom, y);
      }
    }
    if (right < left || bottom < top) return null;
    // A sample pixel of safety retains antialiased edges and fine strokes.
    left = Math.max(0, left - 1); top = Math.max(0, top - 1);
    right = Math.min(canvas.width, right + 2); bottom = Math.min(canvas.height, bottom + 2);
    return { x: left / canvas.width, y: top / canvas.height,
      width: (right - left) / canvas.width, height: (bottom - top) / canvas.height };
  }
  async function measureSvg(markup) {
    const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
    const svg = doc.documentElement;
    if (doc.querySelector('parsererror') || svg.localName !== 'svg') return { markup, bounds: null };
    [svg, ...svg.querySelectorAll('svg')].forEach(node => {
      if (!node.hasAttribute('viewBox') && node.hasAttribute('viewbox')) node.setAttribute('viewBox', node.getAttribute('viewbox'));
      node.removeAttribute('viewbox');
    });
    // A remote image is not rendered inside an SVG loaded as <img>. Embed it
    // before measuring so the crop can never silently discard a photograph.
    try {
      await Promise.all([...svg.querySelectorAll('image')].map(async node => {
        const href = node.getAttribute('href') || node.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || '';
        if (!href || /^data:|^#/i.test(href)) return;
        const response = await fetch(new URL(href, location.href), { mode: 'cors' });
        if (!response.ok) throw new Error('Image unavailable');
        const inline = await read(await response.blob());
        node.setAttribute('href', inline);
        node.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', inline);
      }));
    } catch (_) { return { markup, bounds: null }; }
    let view = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (view.length !== 4 || !view.every(Number.isFinite) || view[2] <= 0 || view[3] <= 0) {
      view = [0, 0, parseFloat(svg.getAttribute('width')), parseFloat(svg.getAttribute('height'))];
    }
    if (!(view[2] > 0 && view[3] > 0)) return { markup, bounds: null };
    const sample = svg.cloneNode(true);
    const scale = 2048 / Math.max(view[2], view[3]);
    sample.setAttribute('width', String(Math.max(1, Math.round(view[2] * scale))));
    sample.setAttribute('height', String(Math.max(1, Math.round(view[3] * scale))));
    sample.setAttribute('viewBox', view.join(' '));
    // Sources remain available to <use>; they do not paint independently.
    let defs = sample.querySelector('defs');
    if (!defs) { defs = doc.createElementNS('http://www.w3.org/2000/svg', 'defs'); sample.prepend(defs); }
    sample.querySelectorAll('#textes_sources,#text_sources,[data-oz-source-only="true"]').forEach(group => {
      while (group.firstChild) defs.append(group.firstChild);
      group.remove();
    });
    const image = await load('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(sample)));
    const pixels = visiblePixels(image, 2048);
    if (!pixels) return { markup, bounds: null };
    const bounds = { x: view[0] + pixels.x * view[2], y: view[1] + pixels.y * view[3],
      width: pixels.width * view[2], height: pixels.height * view[3] };
    const clean = n => String(Math.round(n * 10000) / 10000);
    svg.setAttribute('viewBox', [bounds.x, bounds.y, bounds.width, bounds.height].map(clean).join(' '));
    svg.setAttribute('width', clean(bounds.width));
    svg.setAttribute('height', clean(bounds.height));
    svg.setAttribute('data-oz-tight-frame', 'true');
    return { markup: new XMLSerializer().serializeToString(doc), bounds };
  }
  function prepareSvg(markup) {
    if (!cache.has(markup)) {
      if (cache.size >= 16) cache.delete(cache.keys().next().value);
      cache.set(markup, measureSvg(markup).catch(() => ({ markup, bounds: null })));
    }
    return cache.get(markup);
  }
  async function prepareFile(file) {
    try {
      if (file.type.includes('svg') || /\.svg$/i.test(file.name)) {
        const result = await prepareSvg(await file.text());
        return result.bounds ? new File([result.markup], file.name, { type: 'image/svg+xml' }) : file;
      }
      const image = await load(await read(file));
      const bounds = visiblePixels(image, 4096);
      if (!bounds || (bounds.width === 1 && bounds.height === 1)) return file;
      const x = Math.floor(bounds.x * image.naturalWidth), y = Math.floor(bounds.y * image.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = Math.min(image.naturalWidth - x, Math.ceil(bounds.width * image.naturalWidth));
      canvas.height = Math.min(image.naturalHeight - y, Math.ceil(bounds.height * image.naturalHeight));
      canvas.getContext('2d').drawImage(image, x, y, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      return blob ? new File([blob], file.name.replace(/\.[^.]+$/, '') + '.png', { type: 'image/png' }) : file;
    } catch (_) { return file; }
  }
  window.OazarDesignBounds = { prepareFile, prepareSvg, measureImage: image => visiblePixels(image, 4096) };
})();
